import type { Company } from '@pn/core/domain/intel/company';
import { findOrThrow } from '@pn/core/utils/logic';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isArray, uniq, uniqBy } from 'lodash-es';

export type CompaniesStorageState = {
  isFetching: boolean;
  isError: boolean;
  companies: Company[];
  checkedCompanyIds: Company['id'][];
  checkedCompaniesColors: Record<Company['id'], string>;
  omittedCompanyIds: Company['id'][];
  selectedCompanyId: Company['id'] | undefined;
  companyNameToIdRecord: Record<Company['name'], Company['id']>;
  recentlyViewedCompanyIds: Company['id'][];
};

const initialState: CompaniesStorageState = {
  isFetching: false,
  isError: false,
  companies: [],
  checkedCompanyIds: [],
  checkedCompaniesColors: {},
  omittedCompanyIds: [],
  selectedCompanyId: undefined,
  companyNameToIdRecord: {},
  recentlyViewedCompanyIds: [],
};

export const createCompaniesSlice = (sliceName: string) =>
  createSlice({
    name: sliceName,
    initialState,
    reducers: {
      request: (state) => {
        state.isFetching = true;
        state.isError = false;
      },
      receive: (state, { payload }: PayloadAction<Company[]>) => {
        state.companies = payload;
        state.isFetching = false;
        state.companyNameToIdRecord = payload.reduce(
          (acc, company) => ({
            ...acc,
            [company.name]: company.id,
          }),
          {}
        );
      },
      error: (state) => {
        state.isFetching = false;
        state.isError = true;
      },
      reset: (state) => {
        state.isFetching = false;
        state.isError = false;
        state.companies = [];
        state.checkedCompanyIds = [];
        state.checkedCompaniesColors = {};
      },
      check: (state, { payload }: PayloadAction<Company['id'][]>) => {
        for (const companyId of payload) {
          if (!state.checkedCompanyIds.includes(companyId)) {
            state.checkedCompanyIds.push(companyId);
          }
          if (!state.checkedCompaniesColors[companyId]) {
            state.checkedCompaniesColors[companyId] = getCompanyColor(
              state.checkedCompanyIds.indexOf(companyId)
            );
          }
        }
      },
      uncheck: (state, { payload }) => {
        state.checkedCompanyIds = state.checkedCompanyIds.filter(
          (layerId) => !payload.includes(layerId)
        );
        for (const companyId of payload) {
          delete state.checkedCompaniesColors[companyId];
        }
      },
      omit: (state, { payload }) => {
        state.omittedCompanyIds = uniq([
          ...payload,
          ...state.omittedCompanyIds,
        ]);
      },
      include: (state, { payload }) => {
        state.omittedCompanyIds = state.omittedCompanyIds.filter(
          (companyId) => !payload.includes(companyId)
        );
      },
      uncheckAll: (state) => {
        state.checkedCompanyIds = [];
      },
      includeAll: (state) => {
        state.omittedCompanyIds = [];
      },
      select: (state, { payload }) => {
        state.selectedCompanyId = payload;
      },
      unselect: (state) => {
        state.selectedCompanyId = undefined;
      },
      load: (state, { payload }: PayloadAction<Company | Company[]>) => {
        if (isArray(payload)) {
          state.companies = uniqBy([...state.companies, ...payload], 'id');
        } else {
          state.companies = uniqBy([...state.companies, payload], 'id');
        }
      },
      update: (
        state,
        {
          payload,
        }: PayloadAction<{ companyId: Company['id']; company: Company }>
      ) => {
        const companyToUpdate = findOrThrow(
          state.companies,
          ({ id }) => id === payload.companyId
        );

        Object.assign(companyToUpdate, payload.company, {
          updatedAt: new Date().toISOString(),
        });
      },
      markAsRecentlyViewed: (
        state,
        { payload }: PayloadAction<Company | Company[]>
      ) => {
        const ids = isArray(payload)
          ? payload.map(({ id }) => id)
          : [payload.id];
        state.recentlyViewedCompanyIds = uniq([
          ...ids,
          ...state.recentlyViewedCompanyIds,
        ]);
      },
      setCheckedCompanyColor: (
        state,
        {
          payload,
        }: PayloadAction<{
          companyId: Company['id'];
          color: string;
        }>
      ) => {
        state.checkedCompaniesColors[payload.companyId] = payload.color;
      },
    },
  });

export const companiesSlice = createCompaniesSlice('companies');

const muiColors = [
  '#ffa000', //yellow
  '#f44336', //red
  '#03a9f4', //light blue
  '#1B5E20', //dark green
  // '#fbc02d', //yellow
  '#9c27b0', //purple
  '#009688', //teal
  '#1565c0', //dark blue
  '#ff5722', //orange
  '#795548', //brown
  '#607d8b', //blue grey
  '#673ab7', //deep purple
  '#e91e63', //pink
  '#4caf50', //green
  '#cddc39', //lime
  '#00bcd4', //cyan
  '#3f51b5', //indigo
  '#8bc34a', //light green
  '#2196f3', //blue
  '#ff9800', //deep orange
  '#9e9e9e', //grey
];

// generate hsl color based on index
function getCompanyColor(index: number) {
  if (index < muiColors.length) {
    return muiColors[index];
  }

  const hue = (index * 137.508) % 360;
  return `hsl(${hue}, 75%, 50%)`;
}
